import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useLinesContext } from '../../../contexts/Lines';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import { ILine } from '../../../global';
import {
  lineRowHeight,
  stageTabHeight,
  addStage,
  handleEditStage,
  getLinePosition,
} from '../../../utils';
import { useEditLineContext } from '../../../contexts/EditLine';
import cloneDeep from 'lodash.clonedeep';
import Stages from '../../BuildingBlocks/Lines/Stages/Stages';
import LineActionsEditing from '../LineActionsEditing';
import EmptyLine from '../../BuildingBlocks/Lines/EmptyLine';

const useStyles = makeStyles((theme: any) => ({
  line: {
    display: 'flex',
    position: 'relative',
    height: lineRowHeight - stageTabHeight,
    opacity: ({ lineOnEditMode, lineEdit }: any) => {
      if (lineOnEditMode && !lineEdit) return 0.6;

      return 1;
    },
  },
}));

function removeDeletedStages(line: ILine) {
  let stages = line.stages;
  stages = stages.filter((stage) => !stage.deleted);
  return stages;
}

function getMarginTop(firstProduct: boolean, firstLine: boolean, line: ILine) {
  let marginTop = 0;
  if (firstLine && firstProduct) {
    marginTop = 112;
  }
  if (firstLine && !firstProduct) {
    marginTop = line.empty ? -9 : 1;
  }
  if (!firstLine && line.empty) {
    marginTop = -10;
  }
  return marginTop;
}

export default function LineInTimeline({
  line: originalLine,
  firstLine,
  handleLineSubmit,
  addOrDeleteError,
  firstProduct = false,
  timelineInfo,
  setEditLine = () => {},
}: any) {
  const snackbar = useSnackbarContext();
  const [line, setLine] = useState(originalLine);
  const lineWithAllStages = cloneDeep(line);
  const containerElement = useRef(null);
  const lastPositionData = useRef(null);
  const { lineSummaryRef, leftArrowRef, rightArrowRef } = useEditLineContext();
  const { lineOnEditMode, showLineInTimeline, lineOnShow, setLineOnShow } =
    useLinesContext();
  const [autoSave, setAutoSave] = useState(true);
  const [openStockOnHand, setOpenStockOnHand] = useState(false);

  if (lastPositionData.current) {
    clearTimeout(lastPositionData.current.lastSlideInTimeout);
  }
  if (containerElement.current) {
    containerElement.current.style.transition = `none`;
    containerElement.current.style.marginRight = `0px`;
    containerElement.current.style.marginLeft = `0px`;
  }

  const { createLineClick } = useLinesContext();
  const isEmptyLine = line?.empty;
  if (!isEmptyLine && line) {
    line.stages = removeDeletedStages(line);
  }

  const { startColumn, endColumn } =
    isEmptyLine || !line
      ? { startColumn: 4, endColumn: 10 }
      : getLinePosition(line, timelineInfo);

  const classes = useStyles({ lineOnEditMode, lineEdit: originalLine.edit });

  const updateLine = (line: ILine) => {
    setEditLine(line);
    setLine(line);
  };

  const handleStageUpdate = (
    id: string,
    key: string,
    value: string | number,
    useDebounce: boolean = false,
  ) => {
    handleEditStage(line, updateLine, id, key, value, useDebounce, snackbar);
  };

  function handleAddStage(stageName: string) {
    addStage(stageName, line, updateLine, snackbar);
  }

  function slideElementIntoCurrentPosition(
    currentEdgePosition: number,
    isRightEdgeMeasured: boolean,
  ) {
    if (!containerElement.current) {
      return;
    }
    containerElement.current.style.transition = `margin 0.4s ease-out`;
    containerElement.current.style.marginRight = `0px`;
    containerElement.current.style.marginLeft = `0px`;
    lastPositionData.current.lastEdgePos = currentEdgePosition;
    lastPositionData.current.isRightEdgeMeasured = isRightEdgeMeasured;
  }

  function stickElementToLastPosition(newEdgePosition: number) {
    const xDiff = lastPositionData.current.lastEdgePos - newEdgePosition;
    containerElement.current.style.marginRight = `${-xDiff}px`;
    containerElement.current.style.marginLeft = `${xDiff}px`;
  }

  function setLastPostionOfHiddenLine() {
    const isLocatedAfterTimeline = startColumn === 0;
    if (isLocatedAfterTimeline) {
      const xPosition = containerElement.current.closest(
        '#LinesGridContainer',
      ).clientWidth;
      lastPositionData.current = {
        lastEdgePos: xPosition,
        lastSlideInTimeout: undefined,
        isRightEdgeMeasured: false,
      };
    } else {
      lastPositionData.current = {
        lastEdgePos: 275,
        lastSlideInTimeout: undefined,
        isRightEdgeMeasured: true,
      };
    }
  }

  function calculateCurrentPosition() {
    const currentBoundries = containerElement.current.getBoundingClientRect();
    const rightEdgePosition = currentBoundries.right;
    const leftEdgePosition = currentBoundries.x;
    const isRightEdgeMeasured = startColumn === 1;
    const currentEdgePosition = isRightEdgeMeasured
      ? rightEdgePosition
      : leftEdgePosition;
    const newEdgePosition = lastPositionData.current
      ? lastPositionData.current.isRightEdgeMeasured
        ? rightEdgePosition
        : leftEdgePosition
      : undefined;
    return { isRightEdgeMeasured, currentEdgePosition, newEdgePosition };
  }

  useLayoutEffect(() => {
    if (!showsInTimeline) {
      setLastPostionOfHiddenLine();
    } else {
      const { newEdgePosition, currentEdgePosition, isRightEdgeMeasured } =
        calculateCurrentPosition();
      if (lastPositionData.current) {
        stickElementToLastPosition(newEdgePosition);
        lastPositionData.current.lastSlideInTimeout = setTimeout(() => {
          slideElementIntoCurrentPosition(
            currentEdgePosition,
            isRightEdgeMeasured,
          );
        }, 1);
      } else {
        lastPositionData.current = {
          lastEdgePos: currentEdgePosition,
          isRightEdgeMeasured: isRightEdgeMeasured,
        };
      }
    }
  });

  async function saveLineOnClickOutside(event: MouseEvent) {
    if (
      autoSave &&
      !openStockOnHand &&
      originalLine.edit &&
      !originalLine.editActiveLine &&
      containerElement.current &&
      !containerElement.current.contains(event.target) &&
      lineSummaryRef.current &&
      !lineSummaryRef.current.contains(event.target) &&
      leftArrowRef.current &&
      !leftArrowRef.current.contains(event.target) &&
      rightArrowRef.current &&
      !rightArrowRef.current.contains(event.target)
    ) {
      setAutoSave(false);
      await handleLineSubmit(line);
      setAutoSave(true);
    }
  }

  function setAutoSaveTimeout(state: boolean) {
    if (state === false) setAutoSave(state);
    else
      setTimeout(() => {
        setAutoSave(state);
      }, 500);
  }

  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', saveLineOnClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', saveLineOnClickOutside);
    };

    // eslint-disable-next-line
  }, [containerElement, autoSave]);

  useEffect(() => {
    if (lineOnShow && lineOnShow === line.id) {
      showLineInTimeline(line);
      containerElement.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setLineOnShow(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineOnShow, setLineOnShow]);

  const stagesColumnsCount = endColumn - startColumn;
  const showsInTimeline = stagesColumnsCount > 0;

  return (
    <div
      ref={containerElement}
      className={classes.line}
      style={{
        marginTop: getMarginTop(firstProduct, firstLine, line),
        marginBottom: 0,
        gridColumn: `${startColumn + 1} / ${endColumn + 1}`,
        gridRow: originalLine?.row,
      }}
    >
      {line && (
        <>
          {isEmptyLine && (
            <EmptyLine
              onClick={() => createLineClick(line.product)}
              disabled={lineOnEditMode ? true : false}
            />
          )}
          {!isEmptyLine && (
            <>
              {showsInTimeline && (
                <>
                  <Stages
                    line={line}
                    numColumns={stagesColumnsCount}
                    handleStageUpdate={handleStageUpdate}
                    addStage={handleAddStage}
                    addOrDeleteError={addOrDeleteError}
                    setAutoSave={setAutoSaveTimeout}
                    openStockOnHand={openStockOnHand}
                    setOpenStockOnHand={setOpenStockOnHand}
                  />
                </>
              )}
              {originalLine.edit && (
                <LineActionsEditing
                  line={lineWithAllStages}
                  handleLineSubmit={handleLineSubmit}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
